import React, { useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Switch,
  useDisclosure,
  Text,
  Flex,
  IconButton,
  Spinner,
  Progress,
} from "@chakra-ui/react";
import { EditIcon, AddIcon, AttachmentIcon, DeleteIcon } from "@chakra-ui/icons";
import { useAdminData } from "../../hooks/useAdminData";
import { createToast } from "../../utils/toastUtils";
import { formatDate } from "../../utils/dateUtils";
import { Api } from "../../Api";
import axios from "axios";
import { usePersistentState } from "../../hooks/usePersistentState";

const OTAManagement = () => {
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [filterApplication, setFilterApplication] = usePersistentState(
    "admin:otaManagement:filterApplication",
    ""
  );
  const [activeOnly, setActiveOnly] = usePersistentState(
    "admin:otaManagement:activeOnly",
    false
  );
  const [formData, setFormData] = useState({
    version: "",
    application_name: "",
    s3_bucket: "",
    s3_key: "",
    is_active: true,
    is_latest: false,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const toastHelper = createToast(toast);

  const fetchOTAVersions = useCallback(() => {
    const params = new URLSearchParams();
    if (filterApplication) params.append("application_name", filterApplication);
    if (activeOnly) params.append("active_only", "true");
    return Api.get(`/admin/ota/versions?${params.toString()}`).then((r) => r.data);
  }, [filterApplication, activeOnly]);

  const {
    data: versionsData,
    loading,
    error,
    refetch: fetchVersions,
  } = useAdminData(fetchOTAVersions, [filterApplication, activeOnly]);

  const versions = versionsData?.versions || [];

  const handleCreateVersion = () => {
    setSelectedVersion(null);
    setSelectedFile(null);
    setUploadProgress(0);
    setFormData({
      version: "",
      application_name: "mic",
      s3_bucket: "ethosphere-dev-data",
      s3_key: "",
      is_active: true,
      is_latest: false,
    });
    onOpen();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!formData.version) {
        toastHelper.error("Please enter a version number first");
        event.target.value = null;
        return;
      }
      setSelectedFile(file);
      const appName = formData.application_name || "mic";
      setFormData({
        ...formData,
        s3_bucket: "ethosphere-dev-data",
        s3_key: `ota/${appName}/${formData.version}/${file.name}`,
      });
    }
  };

  const uploadFirmwareToS3 = async (file) => {
    setUploadProgress(0);
    const firmwareUuid = `ota-${Date.now()}`;

    const initiateResponse = await Api.post("/initiate-multipart-upload", {
      type: "ota",
      uuid: firmwareUuid,
      content_type: "application/octet-stream",
      application_name: formData.application_name,
      version: formData.version,
      filename: file.name,
    });

    const { upload_id, file_path } = initiateResponse.data;
    const chunkSize = 5 * 1024 * 1024;
    const numParts = Math.ceil(file.size / chunkSize);
    const parts = [];

    for (let partNumber = 1; partNumber <= numParts; partNumber++) {
      const start = (partNumber - 1) * chunkSize;
      const end = Math.min(partNumber * chunkSize, file.size);
      const partBlob = file.slice(start, end);

      const presignedUrlResponse = await Api.get(
        `/get-part-upload-url?type=ota&upload_id=${upload_id}&part_number=${partNumber}&application_name=${encodeURIComponent(formData.application_name)}&version=${encodeURIComponent(formData.version)}&filename=${encodeURIComponent(file.name)}`
      );

      const { upload_url } = presignedUrlResponse.data;
      const uploadResponse = await axios.put(upload_url, partBlob, {
        headers: { "Content-Type": "" },
        onUploadProgress: (progressEvent) => {
          const overallProgress = Math.round(
            ((partNumber - 1 + progressEvent.loaded / progressEvent.total) / numParts) * 100
          );
          setUploadProgress(overallProgress);
        },
      });

      const etag = uploadResponse.headers.get("etag") || uploadResponse.headers.etag;
      parts.push({ etag, part_number: partNumber });
    }

    const completeResponse = await Api.post("/complete-multipart-upload", {
      type: "ota",
      file_path,
      upload_id,
      parts,
    });

    return completeResponse.data;
  };

  const handleEditVersion = (version) => {
    setSelectedVersion(version);
    setSelectedFile(null);
    setUploadProgress(0);
    setFormData({
      version: version.version,
      application_name: version.application_name,
      s3_bucket: version.s3_bucket,
      s3_key: version.s3_key,
      is_active: version.is_active,
      is_latest: version.is_latest,
    });
    onOpen();
  };

  const handleCloseModal = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setUploading(false);
    onClose();
  };

  const handleSubmit = async () => {
    try {
      if (selectedVersion) {
        setUploading(true);
        await Api.put(`/admin/ota/versions/${selectedVersion.id}`, {
          is_active: formData.is_active,
          is_latest: formData.is_latest,
        });
        toastHelper.success("OTA version updated successfully");
      } else {
        setUploading(true);
        let finalS3Bucket = formData.s3_bucket;
        let finalS3Key = formData.s3_key;

        if (selectedFile) {
          try {
            const uploadResult = await uploadFirmwareToS3(selectedFile);
            finalS3Bucket = uploadResult.bucket_name || formData.s3_bucket;
            finalS3Key = uploadResult.file_path;
          } catch (uploadError) {
            throw new Error(`File upload failed: ${uploadError.message}`);
          }
        }

        await Api.post("/admin/ota/versions", {
          version: formData.version,
          application_name: formData.application_name,
          s3_bucket: finalS3Bucket,
          filename: selectedFile.name,
          is_active: formData.is_active,
          is_latest: formData.is_latest,
          s3_key_override: finalS3Key !== formData.s3_key ? finalS3Key : undefined,
        });

        toastHelper.success("OTA version created successfully");
      }

      fetchVersions();
      handleCloseModal();
    } catch (err) {
      toastHelper.error(err.response?.data?.detail || err.message || "Failed to save OTA version");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteVersion = async (versionId) => {
    if (!window.confirm("Are you sure you want to delete this OTA version?")) return;
    try {
      await Api.delete(`/admin/ota/versions/${versionId}`);
      toastHelper.success("OTA version deleted successfully");
      fetchVersions();
    } catch (err) {
      toastHelper.error(err.response?.data?.detail || "Failed to delete OTA version");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <Spinner size="lg" color="blue.500" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        {error?.message || "Failed to load OTA versions"}
      </Alert>
    );
  }

  return (
    <VStack spacing={5} align="stretch">
      {/* Toolbar */}
      <Flex justify="space-between" align="flex-end" gap={4} wrap="wrap">
        <HStack spacing={4} align="flex-end">
          <FormControl maxW="220px">
            <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
              Application
            </FormLabel>
            <Input
              size="sm"
              placeholder="Filter by app name..."
              value={filterApplication}
              onChange={(e) => setFilterApplication(e.target.value)}
              borderRadius="md"
              bg="white"
            />
          </FormControl>
          <FormControl w="auto">
            <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" mb={1}>
              Active Only
            </FormLabel>
            <HStack h="32px" align="center">
              <Switch
                size="sm"
                isChecked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
            </HStack>
          </FormControl>
        </HStack>

        <Button
          leftIcon={<AddIcon boxSize={3} />}
          colorScheme="blue"
          size="sm"
          px={5}
          borderRadius="md"
          fontWeight="semibold"
          onClick={handleCreateVersion}
        >
          Create Version
        </Button>
      </Flex>

      {/* Table */}
      {versions.length === 0 ? (
        <Box
          py={16} textAlign="center" bg="gray.50" borderRadius="lg"
          borderWidth="1px" borderColor="gray.200" borderStyle="dashed"
        >
          <Text color="gray.400" fontSize="sm">No OTA versions found</Text>
        </Box>
      ) : (
        <Box overflowX="auto" borderRadius="lg" borderWidth="1px" borderColor="gray.200" bg="white">
          <Table variant="unstyled" size="sm">
            <Thead>
              <Tr bg="gray.50" borderBottomWidth="1px" borderBottomColor="gray.200">
                {["Version", "Application", "S3 Bucket", "S3 Key", "Status", "Latest", "Created", ""].map((h) => (
                  <Th key={h} py={3} px={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">{h}</Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {versions.map((version, idx) => (
                <Tr
                  key={version.id}
                  borderBottomWidth={idx < versions.length - 1 ? "1px" : "0"}
                  borderBottomColor="gray.100"
                  _hover={{ bg: "gray.50" }}
                  transition="background 0.1s"
                >
                  <Td py={3} px={4}>
                    <Text fontSize="sm" fontWeight="semibold" fontFamily="mono">{version.version}</Text>
                  </Td>
                  <Td py={3} px={4}>
                    <Badge colorScheme="purple" variant="subtle" borderRadius="full" px={2} fontSize="xs">
                      {version.application_name}
                    </Badge>
                  </Td>
                  <Td py={3} px={4}>
                    <Text fontSize="xs" color="gray.500" fontFamily="mono">{version.s3_bucket}</Text>
                  </Td>
                  <Td py={3} px={4} maxW="200px">
                    <Text fontSize="xs" color="gray.500" fontFamily="mono" isTruncated>{version.s3_key}</Text>
                  </Td>
                  <Td py={3} px={4}>
                    <Badge
                      colorScheme={version.is_active ? "green" : "gray"}
                      variant="subtle" borderRadius="full" px={2} fontSize="xs"
                    >
                      {version.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </Td>
                  <Td py={3} px={4}>
                    {version.is_latest && (
                      <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={2} fontSize="xs">Latest</Badge>
                    )}
                  </Td>
                  <Td py={3} px={4}>
                    <Text fontSize="xs" color="gray.400">{formatDate(version.created_at)}</Text>
                  </Td>
                  <Td py={3} px={4} textAlign="right">
                    <HStack spacing={1} justify="flex-end">
                      <IconButton
                        icon={<EditIcon />} size="xs" colorScheme="blue" variant="ghost"
                        borderRadius="md" aria-label="Edit version"
                        onClick={() => handleEditVersion(version)}
                      />
                      <IconButton
                        icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost"
                        borderRadius="md" aria-label="Delete version"
                        onClick={() => handleDeleteVersion(version.id)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Create/Edit Modal */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="xl">
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" boxShadow="xl">
          <ModalHeader
            pb={3} pt={6} px={6} fontSize="lg" fontWeight="semibold"
            borderBottomWidth="1px" borderBottomColor="gray.100"
          >
            {selectedVersion ? "Edit OTA Version" : "Create OTA Version"}
          </ModalHeader>
          <ModalCloseButton top={4} right={4} />
          <ModalBody px={6} py={5}>
            <VStack spacing={4} align="stretch">
              {!selectedVersion && (
                <Alert status="info" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  Enter version and application name first, then select your firmware file.
                </Alert>
              )}

              <FormControl isRequired isDisabled={selectedVersion !== null}>
                <FormLabel fontSize="sm">Version</FormLabel>
                <Input
                  placeholder="e.g., 1.0.0"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  fontFamily="mono"
                />
                {!selectedVersion && (
                  <Text fontSize="xs" color="orange.600" mt={1}>Enter this before selecting a file</Text>
                )}
              </FormControl>

              <FormControl isRequired isDisabled={selectedVersion !== null}>
                <FormLabel fontSize="sm">Application Name</FormLabel>
                <Input
                  placeholder="mic"
                  value={formData.application_name}
                  onChange={(e) => setFormData({ ...formData, application_name: e.target.value })}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>Default: mic (microphone firmware)</Text>
              </FormControl>

              {!selectedVersion && (
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Firmware Binary</FormLabel>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".bin,.elf,.hex,.ota"
                    style={{ display: "none" }}
                  />
                  <VStack spacing={3} align="stretch">
                    <Button
                      leftIcon={<AttachmentIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      size="sm"
                      variant="outline"
                      borderRadius="md"
                      isDisabled={uploading}
                    >
                      {selectedFile ? "Change File" : "Select Firmware File"}
                    </Button>
                    {selectedFile && (
                      <Box bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.100" px={4} py={3}>
                        <Text fontSize="sm" fontWeight="semibold">{selectedFile.name}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB · s3://{formData.s3_bucket}/{formData.s3_key}
                        </Text>
                      </Box>
                    )}
                  </VStack>
                  <Text fontSize="xs" color="gray.400" mt={2}>
                    File is uploaded to S3 when you click "Create"
                  </Text>
                </FormControl>
              )}

              <FormControl isRequired isDisabled={selectedVersion !== null}>
                <FormLabel fontSize="sm">S3 Bucket</FormLabel>
                <Input
                  placeholder="ethosphere-dev-data"
                  value={formData.s3_bucket}
                  onChange={(e) => setFormData({ ...formData, s3_bucket: e.target.value })}
                  isDisabled={uploading}
                  fontFamily="mono"
                />
              </FormControl>

              <FormControl isRequired isDisabled={selectedVersion !== null}>
                <FormLabel fontSize="sm">S3 Key</FormLabel>
                <Input
                  placeholder="ota/mic/1.0.0/firmware.bin"
                  value={formData.s3_key}
                  onChange={(e) => setFormData({ ...formData, s3_key: e.target.value })}
                  isDisabled={uploading}
                  fontFamily="mono"
                />
                {!selectedVersion && (
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    Structure: ota/{"{application}"}/{"{version}"}/{"{filename}"}
                  </Text>
                )}
              </FormControl>

              <HStack spacing={6}>
                <FormControl display="flex" alignItems="center" w="auto">
                  <Switch
                    isChecked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    mr={2}
                  />
                  <FormLabel mb={0} fontSize="sm">Active</FormLabel>
                </FormControl>

                <FormControl display="flex" alignItems="center" w="auto">
                  <Switch
                    isChecked={formData.is_latest}
                    onChange={(e) => setFormData({ ...formData, is_latest: e.target.checked })}
                    mr={2}
                  />
                  <FormLabel mb={0} fontSize="sm">Latest Version</FormLabel>
                  {formData.is_latest && (
                    <Text fontSize="xs" color="orange.500" ml={2}>
                      Will unset other versions for this app
                    </Text>
                  )}
                </FormControl>
              </HStack>

              {selectedVersion && (
                <Alert status="info" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  Version, application, and S3 paths cannot be changed after creation
                </Alert>
              )}

              {uploading && (
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    {selectedFile ? "Uploading firmware to S3..." : "Processing..."}
                  </Text>
                  <Progress value={uploadProgress} size="sm" colorScheme="blue" hasStripe isAnimated borderRadius="full" />
                  <Text fontSize="xs" color="gray.500" mt={1}>{uploadProgress}% complete</Text>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter px={6} py={4} borderTopWidth="1px" borderTopColor="gray.100" gap={2}>
            <Button variant="ghost" size="sm" onClick={handleCloseModal} isDisabled={uploading}>
              Cancel
            </Button>
            <Button
              colorScheme="blue" size="sm" px={6}
              onClick={handleSubmit}
              isLoading={uploading}
              loadingText={selectedFile && !selectedVersion ? "Uploading..." : "Saving..."}
              isDisabled={
                uploading ||
                !formData.version ||
                !formData.application_name ||
                !formData.s3_bucket ||
                !formData.s3_key ||
                (!selectedVersion && !selectedFile)
              }
            >
              {selectedVersion ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default OTAManagement;
